// 主动消息服务 - 处理角色主动发送消息
import { RealAIService } from './realAiService.js'
import settingsService from './settingsService.js'
import memoryService from './memoryService.js'

class ActiveMessageService {
  /**
   * 计算触发间隔（根据未回复次数）
   * @param {number} noReplyCount - 未回复次数
   * @returns {number} 触发间隔（毫秒）
   */
  calculateTriggerInterval(noReplyCount) {
    const intervals = [60000, 180000, 600000, 1800000] // 1分钟、3分钟、10分钟、30分钟
    return intervals[Math.min(noReplyCount, intervals.length - 1)]
  }

  /**
   * 检查是否需要发送主动消息
   * @param {Object} characterState - 角色状态
   * @param {number} lastMessageTime - 最后一条消息时间戳
   * @param {number} noReplyCount - 未回复次数
   * @returns {boolean} 是否需要发送
   */
  shouldSendActiveMessage(characterState, lastMessageTime, noReplyCount) {
    if (!lastMessageTime) return false
    
    const now = Date.now()
    const timeSinceLastMessage = now - lastMessageTime
    const triggerInterval = this.calculateTriggerInterval(noReplyCount)
    
    // 如果距离最后一条消息的时间超过触发间隔，且最后一条消息是角色发送的
    return timeSinceLastMessage >= triggerInterval
  }

  /**
   * 生成主动消息
   * @param {Object} character - 角色信息
   * @param {Array} conversationHistory - 对话历史
   * @param {number} favorability - 当前好感度
   * @param {number} noReplyCount - 未回复次数
   * @returns {Object} 消息对象
   */
  async generateActiveMessage(character, conversationHistory, favorability, noReplyCount) {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig.apiKey || !languageConfig.apiUrl) {
        return this.generateMockActiveMessage(character, noReplyCount)
      }

      // 检索相关记忆
      const memoryContext = await memoryService.buildMemoryContext(character.id, [])
      
      // 根据未回复次数和好感度选择消息类型
      const messageType = this.selectMessageType(noReplyCount, favorability)
      
      // 获取当前时间信息
      const currentHour = new Date().getHours()
      const timeOfDay = this.getTimeOfDay(currentHour)

      const prompt = `作为${character.name}，用户已经${noReplyCount === 0 ? '1分钟' : noReplyCount === 1 ? '3分钟' : noReplyCount === 2 ? '10分钟' : '30分钟'}没有回复你的消息了。

${memoryContext ? `相关记忆：\n${memoryContext}\n` : ''}

当前好感度：${favorability}/100
当前时间：${timeOfDay}（${currentHour}点）

请生成一条主动消息，要求：
1. 符合角色的性格特点：${character.personality || '友好、开朗'}
2. 根据未回复时间调整语气：
   - 1分钟：轻松、自然，像是在继续话题
   - 3分钟：稍微主动一点，可能是在做什么
   - 10分钟：更主动，表达关心或好奇
   - 30分钟：比较关心，询问是否在忙
3. 根据好感度调整亲密程度：
   - 低好感度（0-30）：礼貌、客气
   - 中好感度（31-70）：友好、亲切
   - 高好感度（71-100）：亲密、关心
4. 根据时间选择合适的话题（${timeOfDay}）
5. 50-100字左右
6. 自然真实，不要过于刻意

${character.description ? `角色背景：${character.description}` : ''}

请生成${character.name}的主动消息：`

      const messageContent = await RealAIService.callLanguageModel(prompt)
      
      return {
        content: messageContent.trim(),
        type: 'text',
        timestamp: Date.now(),
        sender: 'character',
        isActive: true // 标记为主动消息
      }
    } catch (error) {
      console.error('❌ 生成主动消息失败:', error)
      return this.generateMockActiveMessage(character, noReplyCount)
    }
  }

  /**
   * 选择消息类型
   */
  selectMessageType(noReplyCount, favorability) {
    if (noReplyCount === 0) {
      return 'continue' // 继续话题
    } else if (noReplyCount === 1) {
      return 'casual' // 随意询问
    } else if (noReplyCount === 2) {
      return 'curious' // 好奇询问
    } else {
      return 'concerned' // 关心询问
    }
  }

  /**
   * 获取时间段
   */
  getTimeOfDay(hour) {
    if (hour >= 5 && hour < 12) return '早上'
    if (hour >= 12 && hour < 14) return '中午'
    if (hour >= 14 && hour < 18) return '下午'
    if (hour >= 18 && hour < 22) return '晚上'
    return '深夜'
  }

  /**
   * 生成模拟主动消息（降级方案）
   */
  generateMockActiveMessage(character, noReplyCount) {
    const messages = [
      '在做什么呢？',
      '最近怎么样？',
      '想和你聊聊...',
      '你在忙吗？',
      '有时间聊天吗？'
    ]
    
    const message = messages[Math.min(noReplyCount, messages.length - 1)]
    
    return {
      content: message,
      type: 'text',
      timestamp: Date.now(),
      sender: 'character',
      isActive: true
    }
  }
}

export default new ActiveMessageService()

